/**
 * THE CATALOG (2026-09-13, owner: "there is just so much to take in at once"): the items on the
 * canvas as a list, one row per KIND -- the type the authoring tool named (the part of the
 * element's name before Revit's colon), with its type class -- counted, and when the flow lane
 * is on, each kind's roles and where it sits on the potential. Pure: the panel renders rows,
 * the canvas is never consulted, so the grouping is testable on bare elements.
 *
 * PLANE COUNTS (DESIGN_PRINCIPLES 33, depth "counts"): at the project and building rungs,
 * the scope view carries a breakdown by category (plane) instead of elements. The plane count
 * stratum shows this breakdown: one row per category with its count, and an unclassified line
 * only when non-zero. This is never grouped by kind; it is an administrative breakdown by
 * discipline plane alone.
 */
import type { Drawable } from "./queries";
import type { FlowRole, FlowView } from "./flows";
import type { CountsView, ThinRow } from "./queries";

/** One kind of item present: what it is, how many, and its part in the flow. */
export interface CatalogRow {
  /** The row's identity: category, type name and type class together. */
  key: string;
  category: string;
  /** The type the authoring tool named, before the colon. */
  name: string;
  /** The IFC type class, empty when the element carries none. */
  typeClass: string;
  originIds: string[];
  /** How many of the kind hold each role; a kind outside the flow has none. */
  roles: Record<FlowRole, number>;
  /** The kind's mean potential along the flow, null where none of it is solved. */
  phi: number | null;
}

/** The count a kind holds of one role. */
export function roleCount(roles: Record<FlowRole, number>, role: FlowRole): number {
  switch (role) {
    case "source":
      return roles.source;
    case "process":
      return roles.process;
    case "sink":
      return roles.sink;
  }
}

/** One more of the kind holds the role. */
function bumpRole(roles: Record<FlowRole, number>, role: FlowRole): void {
  switch (role) {
    case "source":
      roles.source += 1;
      return;
    case "process":
      roles.process += 1;
      return;
    case "sink":
      roles.sink += 1;
      return;
  }
}

/** The letter a role wears on the canvas and in the catalog: S, P, K. */
export function roleLetter(role: FlowRole): string {
  switch (role) {
    case "source":
      return "S";
    case "process":
      return "P";
    case "sink":
      return "K";
  }
}

/** The type name the authoring tool gave: Revit writes "Type: Instance", the type is before the colon. */
export function typeNameOf(element: Drawable): string {
  if ('raw_attributes' in element && 'object_type' in element) {
    // ViewerElement
    const raw = element.raw_attributes as
      { element_name?: string; ifc_class?: string } | undefined;
    const name = raw?.element_name ?? "";
    const colon = name.indexOf(":");
    const typed = colon > 0 ? name.slice(0, colon) : name;
    return typed.trim() || raw?.ifc_class || "unnamed";
  }
  // ThinRow
  return element.ifc_class || "unnamed";
}

/** The elements grouped by kind, largest kinds first, with what the flow says of each. */
export function catalogOf(
  elements: Drawable[],
  flow: FlowView | null,
): CatalogRow[] {
  const phiAt = new Map<string, number>();
  for (const edge of flow?.edges ?? []) {
    if (edge.phi_a != null) phiAt.set(edge.a, edge.phi_a);
    if (edge.phi_b != null) phiAt.set(edge.b, edge.phi_b);
  }
  const rows = new Map<string, CatalogRow>();
  for (const element of elements) {
    let typeClass = "";
    if ('raw_attributes' in element && 'object_type' in element) {
      // ViewerElement
      const raw = element.raw_attributes as
        { type_class?: string | null } | undefined;
      typeClass = raw?.type_class ?? "";
    }
    const category = element.category ?? "OTHER";
    const name = typeNameOf(element);
    const key = `${category}|${name}|${typeClass}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        key,
        category,
        name,
        typeClass,
        originIds: [],
        roles: { source: 0, process: 0, sink: 0 },
        phi: null,
      };
      rows.set(key, row);
    }
    row.originIds.push(element.origin_id);
    for (const role of flow?.roles?.[element.origin_id] ?? [])
      bumpRole(row.roles, role);
  }
  for (const row of rows.values()) {
    const solved = row.originIds
      .map((id) => phiAt.get(id))
      .filter((p): p is number => p != null);
    row.phi =
      solved.length > 0
        ? solved.reduce((s, p) => s + p, 0) / solved.length
        : null;
  }
  return Array.from(rows.values()).sort(
    (a, b) =>
      b.originIds.length - a.originIds.length || a.name.localeCompare(b.name),
  );
}

/** The role letters a row shows: S, P, K for source, process, sink, each with its count when more than one. */
export function roleText(row: CatalogRow): string {
  const parts: string[] = [];
  const order: FlowRole[] = ["source", "process", "sink"];
  for (const role of order) {
    const letter = roleLetter(role);
    const n = roleCount(row.roles, role);
    if (n === 0) continue;
    parts.push(n === row.originIds.length ? letter : `${letter}${n}`);
  }
  return parts.join(" ");
}

/** One plane (category) and its count at depth "counts" on the scope ladder. */
export interface PlaneCount {
  category: string;
  count: number;
}

/** The planes (categories) and their counts from a scope view at depth "counts", plus unclassified. */
export interface PlaneCounts {
  planes: PlaneCount[];
  unclassified: number;
}

/** Extract the plane breakdown from a counts view: one row per category, sorted by count descending. */
export function planeCountsOf(view: CountsView): PlaneCounts {
  const planes = Object.entries(view.counts)
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);
  return {
    planes,
    unclassified: view.unclassified,
  };
}

/**
 * THE CATALOG AT THE ROWS DEPTH (principle 33): a thin row carries no authored name, so a kind here is
 * the IFC class on its plane -- what the server sent, grouped, never a name invented for it. Origin
 * ids ride so "show this kind alone" still works on the plan.
 */
export function classCatalogOf(rows: ThinRow[]): CatalogRow[] {
  const kinds = new Map<string, CatalogRow>();
  for (const row of rows) {
    const category = row.category ?? "OTHER";
    const key = `${category}|${row.ifc_class}|`;
    let kind = kinds.get(key);
    if (!kind) {
      kind = {
        key,
        category,
        name: row.ifc_class,
        typeClass: "",
        originIds: [],
        roles: { source: 0, process: 0, sink: 0 },
        phi: null,
      };
      kinds.set(key, kind);
    }
    kind.originIds.push(row.origin_id);
  }
  return Array.from(kinds.values()).sort(
    (a, b) =>
      b.originIds.length - a.originIds.length || a.name.localeCompare(b.name),
  );
}
